import type { Seat } from "@ravalmatic/bridge-engine/core";
import { GameConstants } from "../../../GameConstants";
import type { GameOverPlayer } from "./GameOverMenu";

export class GameOverAvatar extends Phaser.GameObjects.Container {

    private static nextTextureId = 0;
    private readonly textureKey: string;

    constructor(scene: Phaser.Scene, public readonly seat: Seat, public readonly player: GameOverPlayer, crownOnRight = false) {

        super(scene);

        const width = 166;
        const height = 176;
        const radius = 22;
        const frame = scene.textures.getFrame("avatar_atlas", `avatar_${player.avatarId}`);
        if (!frame) {
            throw new Error(`Missing atlas frame: avatar_${player.avatarId}`);
        }
        this.textureKey = `game-over-avatar-${GameOverAvatar.nextTextureId++}`;
        const texture = scene.textures.createCanvas(this.textureKey, width, height);
        if (!texture) {
            throw new Error(`Cannot create avatar texture: ${this.textureKey}`);
        }
        const context = texture.getContext();
        context.beginPath();
        context.roundRect(0, 0, width, height, radius);
        context.clip();
        const sourceWidth = frame.cutWidth;
        const sourceHeight = frame.cutHeight;
        const sourceRatio = sourceWidth / sourceHeight;
        const targetRatio = width / height;
        const cropWidth = sourceRatio > targetRatio ? sourceHeight * targetRatio : sourceWidth;
        const cropHeight = sourceRatio > targetRatio ? sourceHeight : sourceWidth / targetRatio;
        context.drawImage(
            frame.source.image,
            frame.cutX + (sourceWidth - cropWidth) / 2,
            frame.cutY + (sourceHeight - cropHeight) / 2,
            cropWidth, cropHeight,
            0, 0, width, height
        );
        texture.refresh();

        const shadow = new Phaser.GameObjects.Graphics(scene);
        shadow.fillStyle(0x000000, .6).fillRoundedRect(-width / 2 + 4, -height / 2 + 8, width, height, radius);
        const portrait = new Phaser.GameObjects.Image(scene, 0, 0, this.textureKey);
        const border = new Phaser.GameObjects.Graphics(scene);
        border.lineStyle(5, 0xffffff, 1).strokeRoundedRect(-width / 2, -height / 2, width, height, radius);
        const cardinalBackground = new Phaser.GameObjects.Graphics(scene);
        const cardinalWidth = 68 * 1.2;
        const cardinalHeight = 30 * 1.2;
        const cardinalX = -cardinalWidth / 2;
        const cardinalCenterY = 87;
        const cardinalY = cardinalCenterY - cardinalHeight / 2;
        cardinalBackground.fillStyle(0x000000, .35)
            .fillRoundedRect(cardinalX, cardinalY + 3, cardinalWidth, cardinalHeight, cardinalHeight / 2);
        cardinalBackground.fillStyle(0x032b54, 1)
            .fillRoundedRect(cardinalX, cardinalY, cardinalWidth, cardinalHeight, cardinalHeight / 2);
        cardinalBackground.lineStyle(2, 0x009fff, 1)
            .strokeRoundedRect(cardinalX, cardinalY, cardinalWidth, cardinalHeight, cardinalHeight / 2);
        const cardinal = this.createLabel(cardinalCenterY, ["N", "E", "S", "W"][seat], 26);
        const profileName = GameConstants.BOT_PROFILES.find(profile => profile.id === player.avatarId)?.name;
        const name = this.createLabel(125, profileName ?? player.name, 28);
        name.setScale(Math.min(1, width / name.width));
        const crown = new Phaser.GameObjects.Image(
            scene, (crownOnRight ? 1 : -1) * (width / 2 - 4), -height / 2 - 12, "texture_atlas_1", "crown_small"
        );
        crown.setScale(crownOnRight ? -1 : 1, 1);
        this.add([shadow, portrait, border, cardinalBackground, cardinal, name, crown]);
    }

    public destroy(fromScene?: boolean): void {

        if (!this.scene) {
            return;
        }
        const textures = this.scene.textures;
        super.destroy(fromScene);
        textures.remove(this.textureKey);
    }

    private createLabel(y: number, text: string, fontSize: number): Phaser.GameObjects.Text {

        return new Phaser.GameObjects.Text(this.scene, 0, y, text, {
            fontFamily: "Baloo2-Bold", fontSize: `${fontSize}px`, color: "#FFFFFF",
            stroke: "#00152E", strokeThickness: 2
        }).setOrigin(.5).setShadow(0, 3, "#00152E", 2, false, true);
    }
}
